import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BillboardButton, Row, FlowGrid, useContainerWidth, Separator, H6 } from '@fs/zion-ui'
import { MenuSearch } from '@fs/zion-icon'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { i18n } from '@fs/zion-locale'
import { getPreference, setPreference } from '@fs/zion-preferences'
import { useLocation } from '@fs/zion-router'
import { useLazyQuery } from '@apollo/client/react'
import { useFeatureFlag } from '@fs/zion-flags'
import { focusableSelectors } from '@fs/zion-frontend-friends'

import { imageSearchAnalytics } from 'helpers/analyticsEvents'
import { surnameFieldLanguages, createSearchCriteria } from 'helpers'
import { useGroupSearch } from 'hooks'
import SnapshotInTimeDialog from 'views/SearchResultsPage/SnapshotInTimeDialog/SnapshotInTimeDialog'
import { getAlternativePlaces } from '../../views/SearchResultsPage/utilities'
import {
  CreatorInput,
  CustodianInput,
  DateInput,
  DgsNumbersInput,
  KeywordInput,
  LifeEventsInput,
  PlaceInput,
  SurnameInput,
  RecordTypesInput,
} from '../FormInputs/FormInputs'
import VolumeSetSearchOverlay from './VolumeSetSearchOverlay'
import transl from './LandingPageSearchForm.transl'
import { getTopLevelPlace as getTopLevelPlaceQuery } from './queries'

const JIAPU_SEARCH_PLACE_IDS = [
  '221', // China
  '10926491', // Hong Kong (NOTE: Possibly not needed anymore since Hong Kong is now a child of China)
  '10926490', // Macau
  '120', // Taiwan
]
const JIAPU_SEARCH_PREFERENCE_KEY = 'lightyear.searchResults.chineseExperience'
const JOKBO_SEARCH_PLACE_ID = '279'
const JOKBO_SEARCH_PREFERENCE_KEY = 'lightyear.searchResults.koreanExperience'
const KEIZU_SEARCH_PLACE_ID = '43'
const KEIZU_SEARCH_PREFERENCE_KEY = 'lightyear.searchResults.japaneseExperience'
const VOLUME_SET_PLACE_IDS = [...JIAPU_SEARCH_PLACE_IDS, JOKBO_SEARCH_PLACE_ID, KEIZU_SEARCH_PLACE_ID]

export default function LandingPageSearchForm() {
  const showSnapshotFlag = useFeatureFlag('lightyear_lightyearReact_showSnapshotInTime')
  const [getTopLevelPlace] = useLazyQuery(getTopLevelPlaceQuery, { errorPolicy: 'all' })
  const jiapuOverlayRef = useRef()
  const jokboOverlayRef = useRef()
  const keizuOverlayRef = useRef()
  const userExpanded = useRef(false)
  const [searchCriteria, setSearchCriteria] = useState()
  const [showAdvancedFields, setShowAdvancedFields] = useState(() => false)
  const [topLevelPlaceId, setTopLevelPlaceId] = useState()
  const [snapshotInTimeSuggestion, setSnapshotInTimeSuggestion] = useState({})
  const { setGroupSearchQueryParams } = useGroupSearch()
  const atWidth = useContainerWidth()

  // the advanced fields mount above the toggle that reveals them -- focus has to be moved in explicitly
  const focusFirstAdvancedField = useCallback((node) => {
    // only a click pulls focus in -- were showAdvancedFields ever seeded true, this would fire on
    // load and strand a reader mid-form
    if (!userExpanded.current) return
    userExpanded.current = false
    node?.querySelector(focusableSelectors)?.focus()
  }, [])

  function toggleAdvancedFields() {
    userExpanded.current = !showAdvancedFields
    setShowAdvancedFields(!showAdvancedFields)
  }

  const location = useLocation()
  const searchResultsPath = `${location.pathname.replace(/\/+$/, '')}/search-results`

  useEffect(() => {
    if (searchCriteria) {
      imageSearchAnalytics()

      if (topLevelPlaceId) {
        let preferenceKey

        if (JIAPU_SEARCH_PLACE_IDS.includes(topLevelPlaceId)) {
          preferenceKey = JIAPU_SEARCH_PREFERENCE_KEY
        } else if (JOKBO_SEARCH_PLACE_ID === topLevelPlaceId) {
          preferenceKey = JOKBO_SEARCH_PREFERENCE_KEY
        } else if (KEIZU_SEARCH_PLACE_ID === topLevelPlaceId) {
          preferenceKey = KEIZU_SEARCH_PREFERENCE_KEY
        }

        if (preferenceKey) {
          getPreference(preferenceKey).then((preference) => {
            if (preference) {
              const otherCriteria = {}

              if (preference === 'jiapu') {
                otherCriteria.volumeSetSearch = 'jiapu'
              } else if (preference === 'jokbo') {
                otherCriteria.volumeSetSearch = 'jokbo'
              } else if (preference === 'keizu') {
                otherCriteria.volumeSetSearch = 'keizu'
              }

              setGroupSearchQueryParams({ ...searchCriteria, ...otherCriteria }, searchResultsPath, false)
            } else {
              let overlay

              if (preferenceKey === JIAPU_SEARCH_PREFERENCE_KEY) {
                overlay = jiapuOverlayRef.current
              } else if (preferenceKey === JOKBO_SEARCH_PREFERENCE_KEY) {
                overlay = jokboOverlayRef.current
              } else if (preferenceKey === KEIZU_SEARCH_PREFERENCE_KEY) {
                overlay = keizuOverlayRef.current
              }

              overlay?.handleClick()
            }
          })
        } else {
          setGroupSearchQueryParams(searchCriteria, searchResultsPath, false)
        }
      } else {
        setGroupSearchQueryParams(searchCriteria, searchResultsPath, false)
      }
    }
  }, [getTopLevelPlace, searchCriteria, searchResultsPath, setGroupSearchQueryParams, topLevelPlaceId])

  async function onPlaceSelected(place) {
    if (!place) {
      setTopLevelPlaceId()
      return
    }

    const response = await getTopLevelPlace({ variables: { placeId: place.key } })

    if (response.data?.getPlace?.topLevelPlaceId) {
      setTopLevelPlaceId(response.data.getPlace.topLevelPlaceId)
    }
  }

  const methods = useZionForm({
    mode: 'all',
    defaultValues: {
      creator: '',
      custodian: '',
      endDate: '',
      imageGroupNumbers: '',
      keyword: '',
      lifeEventRecordTypes: [],
      place: null,
      startDate: '',
      recordTypeIds: [],
    },
  })

  async function updateSearchCriteria(criteria) {
    if (showSnapshotFlag.isOn) {
      const { getValues } = methods
      const { place, endDate, startDate } = getValues()
      const suggested = await getAlternativePlaces(place, endDate, startDate)

      if (suggested) {
        setSnapshotInTimeSuggestion(suggested)
        return
      }
    }

    const safeCriteria = createSearchCriteria(criteria)
    setSearchCriteria({ ...safeCriteria })
  }

  const useVolumeSetOverlay = VOLUME_SET_PLACE_IDS.includes(topLevelPlaceId)
  let volumeSetOverlayConfig

  if (useVolumeSetOverlay) {
    let configKey

    if (JIAPU_SEARCH_PLACE_IDS.includes(topLevelPlaceId)) {
      configKey = 'jiapu'
    } else if (topLevelPlaceId === JOKBO_SEARCH_PLACE_ID) {
      configKey = 'jokbo'
    } else if (topLevelPlaceId === KEIZU_SEARCH_PLACE_ID) {
      configKey = 'keizu'
    }

    volumeSetOverlayConfig = {
      jiapu: {
        description: transl.jiapuDescription(),
        preferenceKey: JIAPU_SEARCH_PREFERENCE_KEY,
        preferenceValue: 'jiapu', // NOTE: preferenceValue is also used to set the volumeSetSearch query param
        question: transl.doYouWantToTryJiapuSearch(),
        ref: jiapuOverlayRef,
        title: transl.jiapuSearchTitle(),
      },
      jokbo: {
        description: transl.jokboDescription(),
        preferenceKey: JOKBO_SEARCH_PREFERENCE_KEY,
        preferenceValue: 'jokbo',
        question: transl.doYouWantToTryJokboSearch(),
        ref: jokboOverlayRef,
        title: transl.jokboSearchTitle(),
      },
      keizu: {
        description: transl.keizuDescription(),
        preferenceKey: KEIZU_SEARCH_PREFERENCE_KEY,
        preferenceValue: 'keizu',
        question: transl.doYouWantToTryKeizuSearch(),
        ref: keizuOverlayRef,
        title: transl.keizuSearchTitle(),
      },
    }[configKey]
  }

  return (
    <>
      <ZionForm methods={methods} onSubmit={updateSearchCriteria}>
        {surnameFieldLanguages.includes(i18n.language) && <SurnameInput />}
        <PlaceInput onPlaceSelected={onPlaceSelected} />

        <Separator size="sm" />

        {showAdvancedFields && (
          <div data-advanced-fields ref={focusFirstAdvancedField}>
            <FlowGrid columnCount={atWidth({ lg: 2, default: 1 })}>
              <LifeEventsInput />
              <DateInput />
            </FlowGrid>

            <Row alignX="center">
              <H6>{transl.advancedSearch()}</H6>
            </Row>

            <RecordTypesInput page="landing" />
            <KeywordInput />

            <FlowGrid columnCount={atWidth({ lg: 2, default: 1 })}>
              <CreatorInput />
              <CustodianInput />
            </FlowGrid>

            <DgsNumbersInput />
          </div>
        )}

        <Row alignX="start">
          <div data-testid="search-button" className="search-form__button">
            <BillboardButton name="submit" emphasis="lightHigh" type="submit" Icon={MenuSearch}>
              {transl.search()}
            </BillboardButton>
          </div>
          <div className="search-form__button">
            <BillboardButton
              aria-expanded={showAdvancedFields}
              emphasis="low"
              name="moreOptions"
              onClick={toggleAdvancedFields}
            >
              {showAdvancedFields ? transl.fewerOptions() : transl.moreOptions()}
            </BillboardButton>
          </div>
        </Row>
        <Separator size="xs" />
        {showSnapshotFlag.isOn && <SnapshotInTimeDialog suggestion={snapshotInTimeSuggestion} />}
      </ZionForm>

      {useVolumeSetOverlay && volumeSetOverlayConfig && (
        <VolumeSetSearchOverlay
          description={volumeSetOverlayConfig.description}
          onPreferenceSet={async (preferVolumeSetSearch) => {
            await setPreference(
              volumeSetOverlayConfig.preferenceKey,
              preferVolumeSetSearch ? volumeSetOverlayConfig.preferenceValue : 'standard'
            )
            setGroupSearchQueryParams(
              {
                ...searchCriteria,
                ...(preferVolumeSetSearch ? { volumeSetSearch: volumeSetOverlayConfig.preferenceValue } : {}),
              },
              searchResultsPath,
              false
            )
          }}
          question={volumeSetOverlayConfig.question}
          ref={volumeSetOverlayConfig.ref}
          title={volumeSetOverlayConfig.title}
        />
      )}
    </>
  )
}
